import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex, Text } from 'rebass';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import { Textarea } from '@rebass/forms';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';
import { gql } from '@/modes/company/gql';

type CancelJobModalProps = {
  onClose: (actionTaken?: boolean) => void;
  assignmentIds: string[] | readonly string[]; // JobIds
};

const CANCEL_ASSIGNMENT = gql(`
  mutation CancelAssignment($assignmentIds: [ID!]!, $comment: String) {
    cancelInboundAssignments(assignmentIds: $assignmentIds, comment: $comment) {
      id
      status
      canceled
    }
  }
`);

const CancelModal: React.FC<CancelJobModalProps> = ({
  onClose,
  assignmentIds,
}) => {
  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => onClose(true), []);
  const { showError } = useNotify();

  const [cancelJob, { loading: mutationLoading }] = useMutation(
    CANCEL_ASSIGNMENT,
    {
      onCompleted: onActionComplete,
      onError: useCallback((error: ApolloError) => {
        showError(
          `Error Cancelling Job${assignmentIds.length > 1 ? 's' : ''}`,
          error
        );
      }, []),
    }
  );

  const [comment, setComment] = useState<string>('');
  const onChange = useCallback(
    (e: any) => {
      setComment(e.target.value);
    },
    [setComment]
  );

  return (
    <Modal
      title={`Cancel ${
        assignmentIds.length > 1 ? assignmentIds.length : ''
      } Job${assignmentIds.length > 1 ? 's' : ''}`}
      onClose={onCancel}
    >
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Text pb={5} variant="body1">
            Are you sure you want to cancel the selected job
            {assignmentIds.length > 1 ? 's' : ''}?
          </Text>
          <Textarea
            height={3 * 16 + 2}
            placeholder="Enter optional comment"
            autoFocus
            onChange={onChange}
          />
          {mutationLoading && <p>Loading...</p>}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={mutationLoading}
          onClick={() => {
            cancelJob({
              variables: {
                assignmentIds: assignmentIds as string[],
                comment,
              },
            });
          }}
        >
          Yes
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          No
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CancelModal.propTypes = {
  onClose: PropTypes.any,
  assignmentIds: PropTypes.any.isRequired,
};

export default CancelModal;
